import { useSuiClient } from '@mysten/dapp-kit';
import { useCallback } from 'react';
import toast from 'react-hot-toast';

import {
	buildClaimStakeRewardsFromFungibleForOwner,
	buildClaimStakeRewardsFromStakedSuiForOwner,
	buildRedeemStakePrincipalForOwner,
	buildRedeemStakePrincipalFromStakedSuiForOwner,
	type StakeQueryClient,
} from '@cpc/tx';

import { useCpcSignAndExecuteTransaction } from '~/hooks/useCpcSignAndExecuteTransaction';

export function useCpcStakeActions(owner: string | undefined) {
	const client = useSuiClient();
	const { mutateAsync: signAndExecute, isPending } = useCpcSignAndExecuteTransaction();

	const runTx = useCallback(
		async (label: string, build: () => Promise<{ toJSON(): Promise<string> }>) => {
			if (!owner) {
				toast.error('请先连接钱包');
				return;
			}
			try {
				const tx = await build();
				await signAndExecute({ transaction: tx });
				toast.success(`${label}成功`);
			} catch (err) {
				console.error(err);
				toast.error(`${label}失败`);
			}
		},
		[owner, signAndExecute],
	);

	const claimFromStakedSui = useCallback(
		(stakedSuiId: string) =>
			runTx('领取奖励', () =>
				buildClaimStakeRewardsFromStakedSuiForOwner(
					client as unknown as StakeQueryClient,
					{ stakedSuiId, recipient: owner! },
				),
			),
		[client, owner, runTx],
	);

	const claimFromFungible = useCallback(
		(fungibleStakedSuiId: string) =>
			runTx('领取奖励', () =>
				buildClaimStakeRewardsFromFungibleForOwner(
					client as unknown as StakeQueryClient,
					{ fungibleStakedSuiId, recipient: owner! },
				),
			),
		[client, owner, runTx],
	);

	const redeemFungible = useCallback(
		(fungibleStakedSuiId: string) =>
			runTx('赎回本金', () =>
				buildRedeemStakePrincipalForOwner(client as unknown as StakeQueryClient, {
					fungibleStakedSuiId,
					recipient: owner!,
				}),
			),
		[client, owner, runTx],
	);

	const redeemFromStakedSui = useCallback(
		(stakedSuiId: string) =>
			runTx('赎回本金', () =>
				buildRedeemStakePrincipalFromStakedSuiForOwner(
					client as unknown as StakeQueryClient,
					{ stakedSuiId, recipient: owner! },
				),
			),
		[client, owner, runTx],
	);

	return {
		isPending,
		claimFromStakedSui,
		claimFromFungible,
		redeemFungible,
		redeemFromStakedSui,
	};
}
